import React from 'react'
import gql from 'graphql-tag'
import styled, { css } from 'styled-components'

import { SIZES, TAG_COLORS, COLORS } from '~/common/globalStyles'
import { TagType, TagsBarTagFragment } from '~/common/types/graphql'
import Icon from '~/views/components/ui/Icon'

interface TagsListProps {
  limitOverflow: boolean
}

export const TagsList = styled.div<TagsListProps>`
  display: flex;
  flex-wrap: wrap;

  ${({ limitOverflow }) =>
    limitOverflow &&
    css`
      overflow-y: hidden;
      height: ${SIZES.medium};
    `};
`
interface TagItemProps {
  colorProp?: TagType['color']
  dense: boolean
  showText: boolean
  noMarginTop: boolean
}
export const TagItem = styled.div<TagItemProps>`
  display: flex;
  align-items: center;
  margin-right: ${SIZES.tiny};
  margin-top: ${SIZES.xxs};
  min-height: ${({ dense }) => SIZES[dense ? 'xxs' : 'xs']};
  padding: 0 ${SIZES.xs};
  font-size: 0;
  border-radius: ${SIZES.xs};
  transition:
    font-size 100ms,
    width 100ms,
    height 100ms;
  overflow: hidden;
  background-color: ${({ colorProp }) =>
    TAG_COLORS[(colorProp as keyof typeof TAG_COLORS) || 'green']};
  ${({ showText, dense, noMarginTop }) =>
    showText
      ? css`
          font-size: ${SIZES[dense ? 'small' : 'medium']};
          margin-right: ${SIZES[dense ? 'tiny' : 'small']};
          margin-top: ${!noMarginTop ? SIZES[dense ? 'xxs' : 'tiny'] : 0};
        `
      : css`
          justify-content: center;
          min-width: ${dense ? '35px' : '41px'};
        `};
`

export const TagText = styled.div`
  font-size: inherit;
  /* font-weight: 700; */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: #ffffff;
`

export const TagIcon = styled(Icon)<TagItemProps>`
  width: ${SIZES.small};
  height: ${SIZES.small};
  margin-right: ${SIZES.tiny};
  ${({ showText, dense }) =>
    !showText
      ? css`
          width: ${SIZES[dense ? 'xxxs' : 'xxs']};
          height: ${SIZES[dense ? 'xxxs' : 'xxs']};
          margin-right: 0;
        `
      : css`
          margin-top: -2px;
        `};
`

interface TagsBarFragments {
  tag: TagsBarTagFragment
}
interface Props {
  tags: TagsBarTagFragment[] | null
  showTagsText: boolean
  tagsListRef?:
    | ((instance: HTMLDivElement | null) => void)
    | React.RefObject<HTMLDivElement | null>
    | null
    | undefined
  dense?: boolean
  noMarginTop?: boolean
  className?: string
}

const TagsBar: FragC<TagsBarFragments, Props> = ({
  tags,
  className,
  showTagsText,
  dense = false,
  tagsListRef,
  noMarginTop = false,
}) => (
  <TagsList
    className={className}
    limitOverflow={dense && !showTagsText}
    ref={tagsListRef}
  >
    {tags &&
      tags.map((tag, index) => (
        <TagItem
          colorProp={tag.color}
          showText={showTagsText}
          key={index}
          dense={dense}
          noMarginTop={noMarginTop}
          data-testid="tag-bar-tag-item"
        >
          {tag.internalOnly && (
            <TagIcon
              name="lock"
              showText={showTagsText}
              color={COLORS.white as 'white'}
              dense={dense}
              noMarginTop={false}
            />
          )}
          <TagText>{tag.name}</TagText>
        </TagItem>
      ))}
  </TagsList>
)

TagsBar.fragments = {
  tag: gql`
    fragment TagsBar_Tag on TagType {
      id
      color
      name
      internalOnly
    }
  `,
}

export default TagsBar
